import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `$${Number(v || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
const STATUS = { active: ['Activo', 'bg-amber-100 text-amber-800'], paid: ['Liquidado', 'bg-emerald-100 text-emerald-800'], cancelled: ['Cancelado', 'bg-slate-200 text-slate-600'] };
const plusDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toLocaleDateString('en-CA'); };
const EMPTY = () => ({ seller_id: '', borrower_phone: '', amount: '', source: '', notes: '', due_date: plusDays(30), reminder_days: 15 });
const FREQS = [['7', 'Semanal'], ['15', 'Quincenal'], ['30', 'Mensual']];
const freqLabel = (d) => (FREQS.find(([v]) => Number(v) === Number(d)) || [null, `Cada ${d} días`])[1];

function FreqPicker({ value, onChange }) {
  const preset = FREQS.some(([v]) => Number(v) === Number(value));
  const [other, setOther] = useState(!preset);
  return (
    <div><Label>¿Cada cuánto paga? *</Label>
      <div className="flex flex-wrap gap-1 mt-1">
        {FREQS.map(([v, lbl]) => (
          <Button key={v} type="button" size="sm" variant={!other && Number(value) === Number(v) ? 'default' : 'outline'}
            onClick={() => { setOther(false); onChange(v); }} data-testid={`loan-freq-${v}`}>{lbl}</Button>
        ))}
        <Button type="button" size="sm" variant={other ? 'default' : 'outline'} onClick={() => setOther(true)} data-testid="loan-freq-other">Otro</Button>
      </div>
      {other && <Input className="mt-2" type="number" min="1" max="365" placeholder="Días entre pagos" value={value} onChange={(e) => onChange(e.target.value)} data-testid="loan-reminder-days" />}
    </div>
  );
}

const fmtDay = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('es-MX', { dateStyle: 'medium' });

const Reminder = () => (
  <div className="text-xs bg-amber-50 border border-amber-300 text-amber-900 rounded p-2 space-y-0.5" data-testid="loan-reminder">
    <div>✉️ Recuerda mantener un <b>sobre individual</b> con todo el dinero de este apartado o préstamo; al liquidar deberá entregarse todo el dinero.</div>
    <div>⚠️ Este dinero es <b>responsabilidad del dependiente</b>, guardarlo en un lugar seguro.</div>
  </div>
);

function LoanForm({ open, initial, mode, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [sellers, setSellers] = useState([]);
  useEffect(() => { if (open) setF(initial || EMPTY()); }, [open, initial]);
  useEffect(() => {
    if (open && mode === 'new') axios.get(`${API}/sellers`, { withCredentials: true })
      .then(({ data }) => setSellers((data || []).filter(s => !s.is_local && s.name !== 'Local'))).catch(() => setSellers([]));
  }, [open, mode]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const titles = { new: 'Nuevo préstamo', edit: 'Modificar préstamo', add: 'Agregar dinero', pay: 'Registrar pago' };

  const submit = async () => {
    setSaving(true);
    try {
      const id = initial?.id;
      const reqs = {
        new: () => axios.post(`${API}/loans`, { ...f, amount: parseFloat(f.amount), reminder_days: parseInt(f.reminder_days, 10) }, { withCredentials: true }),
        edit: () => axios.put(`${API}/loans/${id}`, { borrower_phone: f.borrower_phone, source: f.source, notes: f.notes, due_date: f.due_date || undefined, reminder_days: parseInt(f.reminder_days, 10) || undefined }, { withCredentials: true }),
        add: () => axios.post(`${API}/loans/${id}/add-money`, { amount: parseFloat(f.amount), source: f.source, notes: f.notes }, { withCredentials: true }),
        pay: () => axios.post(`${API}/loans/${id}/payments`, { amount: parseFloat(f.amount), notes: f.notes }, { withCredentials: true }),
      };
      const { data } = await reqs[mode]();
      if (mode === 'pay' && data.status === 'paid') {
        toast.success(`✅ Préstamo liquidado. Entrega ${money(data.paid_amount)} en efectivo del sobre a la caja (ya se sumó al corte como sobrante).`, { duration: 10000 });
      } else toast.success('Guardado y notificado por Telegram');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="max-w-md" data-testid="loan-form-dialog">
        <DialogHeader><DialogTitle>{titles[mode]}{initial?.folio ? ` · ${initial.folio}` : ''}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {mode === 'new' && (
            <div><Label>Vendedor al que se le presta *</Label>
              <select className="w-full h-10 border rounded-md px-3 text-sm bg-white" value={f.seller_id || ''} onChange={set('seller_id')} data-testid="loan-seller-select">
                <option value="">Selecciona un vendedor…</option>
                {sellers.map(s => <option key={s.id} value={s.id}>{s.name}{s.telegram_chat_id ? '' : ' (sin Telegram)'}</option>)}
              </select>
              <p className="text-[11px] text-muted-foreground mt-1">El vendedor recibirá por Telegram los avisos de pagos, faltas de pago y vencimiento.</p>
            </div>
          )}
          {mode === 'edit' && <div className="text-sm">Vendedor: <b>{f.borrower_name}</b></div>}
          {['new', 'edit'].includes(mode) && (<>
            <div><Label>Teléfono</Label><Input value={f.borrower_phone || ''} onChange={set('borrower_phone')} placeholder="Si se deja vacío, se usa el del vendedor" data-testid="loan-phone" /></div>
            <div><Label>Fecha límite *</Label><Input type="date" value={f.due_date || ''} onChange={set('due_date')} data-testid="loan-due-date" /></div>
            <FreqPicker key={initial?.id || 'new'} value={f.reminder_days ?? 15} onChange={(v) => setF({ ...f, reminder_days: v })} />
            <p className="text-[11px] text-muted-foreground">Si pasa ese tiempo sin pago, o vence la fecha límite, Telegram avisará cada día.</p>
          </>)}
          {['new', 'add', 'pay'].includes(mode) && (
            <div><Label>{mode === 'pay' ? 'Monto del pago *' : 'Monto *'}</Label>
              <Input type="number" min="0" step="0.01" value={f.amount} onChange={set('amount')} data-testid="loan-amount" />
              {mode === 'pay' && <p className="text-[11px] text-muted-foreground mt-1">Resta: {money(initial?.remaining_amount)}</p>}
            </div>
          )}
          {['new', 'edit', 'add'].includes(mode) && (
            <div><Label>¿De dónde salió el dinero? *</Label>
              <Input value={f.source || ''} onChange={set('source')} placeholder="Ej: sobre del corte del 12/10, caja chica…" data-testid="loan-source" />
            </div>
          )}
          <div><Label>Notas</Label><Input value={f.notes || ''} onChange={set('notes')} data-testid="loan-notes" /></div>
          {mode !== 'add' && <Reminder />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={() => (mode === 'add' ? setConfirm(true) : submit())} disabled={saving} data-testid="loan-form-submit">{saving ? 'Guardando…' : 'Guardar'}</Button>
        </DialogFooter>
      </DialogContent>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="loan-add-money-popup">
          <AlertDialogHeader>
            <AlertDialogTitle>✉️ Antes de agregar dinero</AlertDialogTitle>
            <AlertDialogDescription asChild><div><Reminder /></div></AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="loan-add-money-popup-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirm(false); submit(); }} data-testid="loan-add-money-popup-accept">Entendido, agregar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

export const LoansSection = () => {
  const [loans, setLoans] = useState([]);
  const [form, setForm] = useState({ open: false, mode: 'new', initial: null });
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/loans?include_history=true`, { withCredentials: true });
      setLoans(data);
    } catch { toast.error('Error al cargar préstamos'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = async (l) => {
    const reason = window.prompt(`Motivo para cancelar ${l.folio}:`);
    if (reason === null) return;
    try {
      await axios.post(`${API}/loans/${l.id}/cancel`, { reason }, { withCredentials: true });
      toast.success('Préstamo cancelado'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const active = loans.filter(l => l.status === 'active');
  const open = (mode, l = null) => setForm({ open: true, mode, initial: l ? { ...l, amount: '', notes: '', source: mode === 'add' ? '' : l.source } : null });

  return (
    <div className="space-y-4" data-testid="loans-section">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-violet-600 text-white rounded-sm p-4">
          <p className="text-xs uppercase tracking-wide opacity-80">✉️ Sobres de préstamos</p>
          <p className="text-3xl font-bold mt-1" data-testid="loans-cash-box">{money(active.reduce((s, l) => s + (l.paid_amount || 0), 0))}</p>
          <p className="text-[11px] opacity-70 mt-1">Pagos recibidos · entra al corte al liquidar</p>
        </div>
        <div className="bg-white border rounded-sm p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Por cobrar</p>
          <p className="text-3xl font-bold mt-1">{money(active.reduce((s, l) => s + (l.remaining_amount || 0), 0))}</p>
          <p className="text-[11px] text-muted-foreground mt-1">{active.length} préstamos activos</p>
        </div>
        <div className="flex items-center justify-end">
          <Button onClick={() => open('new')} className="bg-violet-600 hover:bg-violet-700" data-testid="new-loan-button">+ Nuevo préstamo</Button>
        </div>
      </div>
      <Reminder />
      {loans.length === 0 ? <div className="text-center text-sm text-muted-foreground py-10">Sin préstamos registrados</div> : (
        <div className="space-y-2">
          {loans.map(l => {
            const [lbl, cls] = l.overdue ? ['Vencido', 'bg-rose-600 text-white'] : (STATUS[l.status] || [l.status, '']);
            const late = l.status === 'active' && l.days_without_payment >= l.reminder_days;
            return (
              <div key={l.id} className={`bg-white border rounded-lg p-3 ${l.overdue ? 'border-rose-500 bg-rose-50 border-2' : l.status === 'active' ? 'border-amber-300' : ''}`} data-testid={`loan-${l.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="font-mono text-xs text-muted-foreground">{l.folio} · {fmtDate(l.created_at)}</div>
                    <div className="font-semibold">{l.borrower_name}{l.borrower_phone ? ` · ${l.borrower_phone}` : ''}</div>
                    {l.due_date && (
                      <div className={`text-xs ${l.overdue ? 'text-rose-700 font-bold' : 'text-slate-600'}`} data-testid={`loan-due-${l.id}`}>
                        📅 Fecha límite: {fmtDay(l.due_date)}{l.overdue ? ` · VENCIDO hace ${l.days_overdue} días` : ''}
                      </div>
                    )}
                    {l.status === 'active' && (
                      <div className={`text-xs ${late ? 'text-rose-700 font-semibold' : 'text-slate-500'}`} data-testid={`loan-days-${l.id}`}>
                        ⏰ {l.days_without_payment} días sin pago · paga {freqLabel(l.reminder_days).toLowerCase()}
                      </div>
                    )}
                    <div className="text-xs text-slate-600">🏦 Salió de: {l.source}{l.notes ? ` · 📝 ${l.notes}` : ''}</div>
                  </div>
                  <div className="text-right text-sm">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>{lbl}</span>
                    <div className="mt-1">Prestado <b>{money(l.amount)}</b></div>
                    <div className="text-xs">Pagado {money(l.paid_amount)} · Resta <b className="text-rose-600">{money(l.remaining_amount)}</b></div>
                  </div>
                </div>
                {(l.history || []).length > 0 && (
                  <details className="mt-2 text-xs text-slate-600">
                    <summary className="cursor-pointer">Movimientos ({l.history.length})</summary>
                    <ul className="mt-1 space-y-0.5">
                      {l.history.map((h, i) => (
                        <li key={i}>{fmtDate(h.at)} · <b>{h.action}</b>{h.amount ? ` ${money(h.amount)}` : ''}{h.source ? ` (de ${h.source})` : ''}{h.reason ? ` · ${h.reason}` : ''} · {h.by}</li>
                      ))}
                    </ul>
                  </details>
                )}
                {l.status === 'active' && (
                  <div className="mt-2 flex flex-wrap gap-2 justify-end">
                    {isAdmin && <Button size="sm" variant="outline" onClick={() => open('edit', l)} data-testid={`loan-edit-${l.id}`}>✏️ Modificar</Button>}
                    {isAdmin && <Button size="sm" variant="outline" onClick={() => open('add', l)} data-testid={`loan-add-${l.id}`}>➕ Agregar dinero</Button>}
                    <Button size="sm" variant="outline" className="text-rose-600 border-rose-300" onClick={() => cancel(l)} data-testid={`loan-cancel-${l.id}`}>Cancelar</Button>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => open('pay', l)} data-testid={`loan-pay-${l.id}`}>💵 Registrar pago</Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <LoanForm open={form.open} mode={form.mode} initial={form.initial}
        onClose={() => setForm({ ...form, open: false })} onSaved={() => { setForm({ ...form, open: false }); load(); }} />
    </div>
  );
};
